import React, { useState } from "react";
import {
  Mail,
  MapPin,
  ArrowRight,
  ShieldCheck,
  Users,
  Lightbulb,
  Flame,
  Target,
  MousePointerClick,
  FileText,
  CheckCircle,
  AlertCircle,
  Settings,
  PenTool,
  MessageSquare,
  AlertTriangle,
  X,
  Info,
} from "lucide-react";
import campaignData from "../../data/campaignData.json";
import Modal from "../ui/Modal";
import TabOverviewCard from "./TabOverviewCard"; // Reusable Narrative Component

const BestPracticeBlueprintsCard = () => {
  const [activeRegion, setActiveRegion] = useState("India");
  const [activeModal, setActiveModal] = useState(null);
  const [selectedAsset, setSelectedAsset] = useState(null);

  // Safe access to data with fallbacks
  const blueprintData = campaignData?.nurture_blueprints || {
    streams: [],
    regional_nuance: {},
  };
  const governanceData = campaignData?.system_governance || {
    definition: { is: [], is_not: [] },
    design_principle: "",
  };
  // Pulling the new "Strategic Narrative" for this specific tab
  const narrative = campaignData.tab_narratives?.blueprints;

  const { streams, regional_nuance } = blueprintData;

  const handleAssetClick = (asset) => {
    setSelectedAsset(asset);
    setActiveModal("asset");
  };

  // Visual Helpers
  const getIntentColor = (level) => {
    switch (level?.toLowerCase()) {
      case "high":
        return "text-red-500";
      case "medium":
        return "text-amber-500";
      case "low":
        return "text-blue-400";
      default:
        return "text-slate-400";
    }
  };

  const getStreamIcon = (id) => {
    switch (id) {
      case "stream_1":
        return <Lightbulb className="w-5 h-5 text-amber-500" />;
      case "stream_2":
        return <Users className="w-5 h-5 text-blue-500" />;
      case "stream_3":
        return <ShieldCheck className="w-5 h-5 text-emerald-500" />;
      default:
        return <Mail className="w-5 h-5" />;
    }
  };

  const getStreamColor = (id) => {
    switch (id) {
      case "stream_1":
        return "border-amber-200 bg-amber-50";
      case "stream_2":
        return "border-blue-200 bg-blue-50";
      case "stream_3":
        return "border-emerald-200 bg-emerald-50";
      default:
        return "border-slate-200 bg-slate-50";
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-6 bg-white rounded-lg shadow-sm font-sans animate-in fade-in duration-500">
      {/* 1. EXECUTIVE OVERVIEW (Strategic Layer) */}
      {narrative && (
        <TabOverviewCard narrative={narrative} iconType="blueprints" />
      )}

      {/* 2. GOVERNANCE BLOCK (System Definition) */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-bold text-slate-800 text-lg">
            System Governance
          </h3>
          <div className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded text-xs font-bold uppercase tracking-wider border border-emerald-200">
            v3.0: Blueprint Mode Active
          </div>
        </div>

        <div className="bg-slate-50 rounded-xl p-4 border border-slate-200 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="text-xs font-bold text-emerald-600 uppercase tracking-wider mb-2 flex items-center gap-1">
              <CheckCircle size={12} /> This System IS:
            </h4>
            <ul className="space-y-1">
              {governanceData.definition.is.map((item, i) => (
                <li key={i} className="text-sm text-slate-700">
                  • {item}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1">
              <X size={12} /> This System IS NOT:
            </h4>
            <ul className="space-y-1">
              {governanceData.definition.is_not.map((item, i) => (
                <li key={i} className="text-sm text-slate-500">
                  • {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* 3. REGION TOGGLE */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 bg-slate-100 p-1 rounded-lg">
            <button
              onClick={() => setActiveRegion("India")}
              className={`px-4 py-2 text-sm font-semibold rounded-md transition-all ${
                activeRegion === "India"
                  ? "bg-white text-emerald-700 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              India Strategy
            </button>
            <button
              onClick={() => setActiveRegion("SEA")}
              className={`px-4 py-2 text-sm font-semibold rounded-md transition-all ${
                activeRegion === "SEA"
                  ? "bg-white text-emerald-700 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              SEA Strategy
            </button>
          </div>
          <div className="flex items-center gap-3 text-sm text-slate-600 bg-slate-50 px-4 py-2 rounded-full border border-slate-200">
            <MapPin className="w-4 h-4 text-emerald-500" />
            <span className="font-semibold">{activeRegion}:</span>
            <span className="italic">{regional_nuance[activeRegion]?.[0]}</span>
          </div>
        </div>
        {/* Helper Text */}
        <p className="text-xs text-slate-400 mt-2 flex items-center gap-1">
          <Info size={12} />
          This changes the behavioral blueprint (tone + cadence philosophy), not
          the segment rules.
        </p>
      </div>

      {/* 4. TIMELINE VISUALIZATION */}
      <div className="relative">
        <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-slate-200" />

        <div className="space-y-10">
          {streams.map((stream) => (
            <div key={stream.id} className="relative pl-20">
              <div className="absolute left-0 top-0 flex flex-col items-center w-16">
                <div className="bg-slate-100 text-slate-600 text-xs font-bold py-1 px-2 rounded mb-2 border border-slate-200 shadow-sm">
                  {stream.timeline}
                </div>
              </div>

              <div
                className={`border rounded-xl p-6 ${getStreamColor(
                  stream.id
                )} transition-all hover:shadow-md`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-white rounded-xl shadow-sm border border-slate-100">
                      {getStreamIcon(stream.id)}
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-800 text-lg flex items-center gap-2">
                        {stream.name}
                        <span className="text-xs font-normal text-slate-500 px-2 py-0.5 bg-white/60 rounded-full border border-slate-200">
                          {stream.phase}
                        </span>
                      </h3>
                      <p className="text-slate-600 text-sm mt-1">
                        {stream.goal}
                      </p>
                    </div>
                  </div>
                </div>

                {/* SALES READINESS LINE */}
                <div className="mb-6 bg-white/60 p-2 rounded-lg border border-slate-200/50 flex items-center gap-2">
                  <Target size={14} className="text-purple-600" />
                  <p className="text-xs font-medium text-purple-800">
                    <span className="uppercase tracking-wide text-purple-600/70 mr-2 font-bold">
                      Sales Outcome:
                    </span>
                    {stream.sales_readiness_outcome}
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {stream.assets.map((asset, i) => (
                    <div
                      key={i}
                      onClick={() => handleAssetClick(asset)}
                      className="bg-white p-5 rounded-lg border border-slate-200 shadow-sm hover:border-emerald-400 hover:shadow-md transition-all cursor-pointer group relative overflow-hidden"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded bg-slate-100 text-slate-600">
                            {asset.type}
                          </span>
                          {asset.leap_tier && (
                            <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-1 rounded border border-emerald-100">
                              {asset.leap_tier.split(":")[0]}
                            </span>
                          )}
                        </div>
                        <div
                          className="flex items-center gap-1"
                          title={`Intent Level: ${asset.intent_level}`}
                        >
                          <Flame
                            className={`w-4 h-4 ${getIntentColor(
                              asset.intent_level
                            )}`}
                            fill="currentColor"
                          />
                        </div>
                      </div>
                      <h4 className="font-bold text-slate-800 mb-1 group-hover:text-emerald-700 transition-colors">
                        {asset.subject}
                      </h4>
                      <p className="text-xs text-slate-500 leading-relaxed mb-3 border-b border-slate-100 pb-3">
                        {asset.content_focus}
                      </p>
                      <div className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-1.5 text-slate-500">
                          <Target className="w-3.5 h-3.5" />
                          <span className="font-medium">
                            {asset.buying_role}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5 text-emerald-600 font-semibold opacity-0 group-hover:opacity-100 transition-opacity">
                          <span>View Advisory Guide</span>
                          <ArrowRight className="w-3.5 h-3.5" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* FOOTER & LEAP AUDIT */}
      <div className="mt-12 bg-slate-900 rounded-xl text-white shadow-lg overflow-hidden">
        <div className="p-5 flex items-center justify-between border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-500/20 rounded-lg">
              <ShieldCheck className="w-6 h-6 text-emerald-400" />
            </div>
            <div className="text-sm">
              <span className="font-bold block text-emerald-100">
                LEAP Compliance Active
              </span>
              <span className="text-slate-400">
                All streams currently meet Tier 1 & Tier 2 assurance standards.
              </span>
            </div>
          </div>
          <button
            onClick={() => setActiveModal("leap")}
            className="text-xs font-semibold bg-emerald-600 hover:bg-emerald-500 px-4 py-2 rounded-lg transition-colors shadow-sm"
          >
            View LEAP Audit Log
          </button>
        </div>
        <div className="p-4 bg-black/30 text-center">
          <p className="text-xs text-slate-400 font-mono tracking-wide">
            <span className="text-emerald-500 font-bold mr-2">
              DESIGN PRINCIPLE:
            </span>
            "{governanceData.design_principle}"
          </p>
        </div>
      </div>

      {/* ASSET MODAL */}
      <Modal
        isOpen={activeModal === "asset" && selectedAsset}
        onClose={() => setActiveModal(null)}
        title={
          selectedAsset
            ? `Advisory Guide: ${selectedAsset.subject}`
            : "Asset Details"
        }
      >
        {selectedAsset && selectedAsset.advisory_guide && (
          <div className="space-y-6">
            {/* Risk Warning */}
            <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg flex gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
              <div>
                <h5 className="text-xs font-bold text-amber-800 uppercase tracking-wide">
                  Risk of Skipping
                </h5>
                <p className="text-sm text-amber-900">
                  {selectedAsset.risk_of_skipping}
                </p>
              </div>
            </div>

            {/* Core Narrative */}
            <div className="bg-slate-50 p-5 rounded-xl border border-slate-200">
              <div className="flex items-center gap-2 mb-3">
                <MessageSquare className="w-5 h-5 text-emerald-600" />
                <h4 className="font-bold text-slate-800">
                  Buyer Message & Core Narrative
                </h4>
              </div>
              <p className="text-sm font-medium text-slate-800 italic mb-4">
                "{selectedAsset.advisory_guide.core_narrative}"
              </p>
              <ul className="space-y-2">
                {selectedAsset.advisory_guide.key_points.map((point, idx) => (
                  <li
                    key={idx}
                    className="flex items-start gap-2 text-sm text-slate-600"
                  >
                    <span className="mt-1.5 w-1.5 h-1.5 bg-emerald-400 rounded-full shrink-0" />
                    {point}
                  </li>
                ))}
              </ul>
            </div>

            {/* Intent Stage Badge */}
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-slate-400 uppercase">
                Blueprint Intent Stage:
              </span>
              <span className="text-xs font-bold bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                {selectedAsset.intent_maturity_stage}
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <PenTool className="w-4 h-4 text-blue-600" />
                  <h4 className="font-bold text-slate-800 text-sm uppercase tracking-wide">
                    Action Items — Marketing
                  </h4>
                </div>
                <ul className="space-y-3">
                  {selectedAsset.advisory_guide.marketing_actions.map(
                    (action, idx) => (
                      <li
                        key={idx}
                        className="text-sm text-slate-600 flex gap-2"
                      >
                        <span className="text-blue-400">•</span>
                        {action}
                      </li>
                    )
                  )}
                </ul>
              </div>
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Settings className="w-4 h-4 text-purple-600" />
                  <h4 className="font-bold text-slate-800 text-sm uppercase tracking-wide">
                    Action Items — Marketing Ops
                  </h4>
                </div>
                <ul className="space-y-3">
                  {selectedAsset.advisory_guide.ops_actions.map(
                    (action, idx) => (
                      <li
                        key={idx}
                        className="text-sm text-slate-600 flex gap-2"
                      >
                        <span className="text-purple-400">•</span>
                        {action}
                      </li>
                    )
                  )}
                </ul>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* LEAP MODAL */}
      <Modal
        isOpen={activeModal === "leap"}
        onClose={() => setActiveModal(null)}
        title="LEAP Compliance Audit Log"
      >
        <div className="space-y-6">
          <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
            <div className="flex items-center gap-4 mb-2">
              <FileText className="text-emerald-600 w-5 h-5" />
              <h4 className="font-bold text-slate-800 text-sm">
                System Validation Summary
              </h4>
            </div>
            <p className="text-xs text-slate-500 leading-relaxed">
              This campaign has passed all 3 tiers of the Lead Evaluation &
              Assurance Process (LEAP). Leads are automatically filtered based
              on engagement quality before sales handoff.
            </p>
          </div>
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-100 text-slate-600 font-semibold border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3">Assurance Tier</th>
                  <th className="px-4 py-3">Validation Check</th>
                  <th className="px-4 py-3 text-right">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                <tr className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <span className="font-bold text-slate-800 block">
                      Tier 1: Intelligence
                    </span>
                    <span className="text-xs text-slate-500">
                      Awareness Phase
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-600 text-xs">
                    <div className="font-semibold text-slate-700">
                      • Verified Account Vetting (ABM)
                    </div>
                    <div className="text-slate-500">
                      Manual pre-vetting of accounts vs. broad ICP signals
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="inline-flex items-center gap-1 text-emerald-600 font-bold text-xs bg-emerald-50 px-2 py-1 rounded-full">
                      <CheckCircle size={12} /> Pass
                    </span>
                  </td>
                </tr>
                <tr className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <span className="font-bold text-slate-800 block">
                      Tier 2: Profiling
                    </span>
                    <span className="text-xs text-slate-500">
                      Engagement Phase
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-600 text-xs">
                    <div className="font-semibold text-slate-700">
                      • Explicit Qualification Gate
                    </div>
                    <div className="text-slate-500">
                      Custom qualification question passed
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="inline-flex items-center gap-1 text-emerald-600 font-bold text-xs bg-emerald-50 px-2 py-1 rounded-full">
                      <CheckCircle size={12} /> Pass
                    </span>
                  </td>
                </tr>
                <tr className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <span className="font-bold text-slate-800 block">
                      Tier 3: Adaptive Scoring
                    </span>
                    <span className="text-xs text-slate-500">
                      Handoff Phase
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-600 text-xs">
                    <div className="font-semibold text-slate-700">
                      • Buying Group Penetration
                    </div>
                    <div className="text-slate-500">
                      Stakeholder mapping & Nurture response triggers
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="inline-flex items-center gap-1 text-blue-600 font-bold text-xs bg-blue-50 px-2 py-1 rounded-full">
                      <AlertCircle size={12} /> Active
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="flex justify-end">
            <button className="text-xs text-slate-400 hover:text-emerald-600 underline">
              Download Full Compliance Report (PDF)
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default BestPracticeBlueprintsCard;
